/**
 * FP3-034B — Assessment Documents card.
 *
 * Renders inside the Student Workspace > Assessments tab so providers
 * can upload, view, download, archive, and delete assessment-specific
 * artifacts (probes, screeners, formal/informal tests) without leaving
 * the Assessments context.
 *
 * Storage / privacy
 * ─────────────────
 * Uses the SAME `student_documents` collection + endpoints as the
 * Documents tab, but the row records carry `evidence_scope =
 * "assessment"` so this card's listing is server-side filtered to
 * just the Assessments pool. Uploads from Documents stay in
 * Documents; uploads from here stay here — unless the provider
 * explicitly reclassifies a doc via PATCH `evidence_scope`.
 *
 * Both pools (general + assessment) remain selectable in the Goal
 * Plan AI document picker.
 *
 * Document types
 * ──────────────
 * The Assessments upload modal limits the doc-type dropdown to
 * assessment-leaning labels (academic_assessment, intervention_data,
 * work_sample, evaluation_report, speech_language_report, other).
 * The scope, not the type, is what splits the tabs.
 */
import { useEffect, useState } from "react";
import { ClipboardText, UploadSimple, Warning } from "@phosphor-icons/react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import {
  DocumentRow,
  UploadModal,
} from "@/components/student/StudentDocumentsTab";
import DocumentViewerModal from "@/components/student/DocumentViewerModal";

// FP3-034B correction — Doc type options shown in the Assessments
// upload modal. These bias toward the kinds of artifacts an
// assessment-tab upload typically is (probe data, work samples,
// short screeners), while still allowing full reports if a provider
// uploads a complete eval here.
const ASSESSMENT_DOC_TYPES = [
  { value: "academic_assessment", label: "Academic assessment / probe" },
  { value: "intervention_data", label: "Intervention / probe data" },
  { value: "work_sample", label: "Work sample" },
  { value: "evaluation_report", label: "Evaluation report" },
  { value: "speech_language_report", label: "Speech / language report" },
  { value: "other", label: "Other supporting artifact" },
];

export default function StudentAssessmentDocumentsCard({ studentId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewerIsAdmin, setViewerIsAdmin] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);

  const load = async ({ background = false } = {}) => {
    if (!background) setLoading(true);
    setError("");
    try {
      // Server-side scope filter — the Documents tab does the same
      // with `evidence_scope=general`. Anything uploaded as a
      // "general" doc never crosses over.
      const res = await api.listStudentDocuments(studentId, {
        evidence_scope: "assessment",
      });
      setDocs(res.data?.items || []);
      setViewerIsAdmin(!!res.data?.viewer_is_admin);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setError("You don't have access to this student's documents.");
      } else {
        setError(
          err?.response?.data?.detail || "Could not load assessment documents"
        );
      }
    } finally {
      if (!background) setLoading(false);
    }
  };

  useEffect(() => {
    if (!studentId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  if (error) {
    return (
      <Card
        className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm"
        data-testid="assessment-docs-error"
      >
        <CardContent className="p-6 text-center text-[13px] text-[#7A3A2C]">
          <Warning size={16} weight="duotone" className="inline mr-1 mb-0.5" />
          {error}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm"
      data-testid="assessment-docs-card"
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <h3 className="text-[13.5px] font-semibold text-[#1F2937] heading-font flex items-center gap-1.5">
            <ClipboardText
              size={14}
              weight="duotone"
              className="text-[#3F4756]"
            />
            Assessment artifacts
            <span
              className="ml-1.5 text-[11px] font-medium text-[#6B7280]"
              data-testid="assessment-docs-count"
            >
              · {docs.length}
            </span>
          </h3>
          <Button
            type="button"
            data-testid="assessment-docs-upload-btn"
            size="sm"
            onClick={() => setUploadOpen(true)}
            className="h-8 text-[12px] bg-[#1E3F33] hover:bg-[#173127] text-white"
          >
            <UploadSimple size={12} className="mr-1" />
            Upload assessment artifact
          </Button>
        </div>

        <p className="text-[11.5px] text-[#6B7280] mb-3">
          Tests, probes, screeners, and assessment-specific artifacts.
          These stay scoped to Assessments — they don’t appear in the
          general Documents list unless you reclassify them. Goal Plan
          AI can still select them as evidence.
        </p>

        {loading ? (
          <p
            className="text-[12.5px] text-[#6B7280] py-4 text-center"
            data-testid="assessment-docs-loading"
          >
            Loading assessment artifacts…
          </p>
        ) : docs.length === 0 ? (
          <p
            className="text-[12.5px] text-[#6B7280] py-6 text-center"
            data-testid="assessment-docs-empty"
          >
            No assessment artifacts on file yet. Upload a probe, work
            sample, or short screener to get started. Full evaluation
            reports usually live on the Documents tab.
          </p>
        ) : (
          <ul className="divide-y divide-[#F1F2F4] border border-[#F1F2F4] rounded-lg">
            {docs.map((d) => (
              <DocumentRow
                key={d.id}
                doc={d}
                studentId={studentId}
                viewerIsAdmin={viewerIsAdmin}
                onChanged={() => load({ background: true })}
                onView={(toShow) => setViewingDoc(toShow)}
              />
            ))}
          </ul>
        )}
      </CardContent>

      {uploadOpen && (
        <UploadModal
          studentId={studentId}
          isAdmin={viewerIsAdmin}
          titleLabel="Assessment"
          defaultType="academic_assessment"
          typeOptions={ASSESSMENT_DOC_TYPES}
          evidenceScope="assessment"
          onClose={() => setUploadOpen(false)}
          onUploaded={async (newDoc) => {
            // The server stamps evidence_scope on the upload, so the
            // optimistic append is safe regardless of doc type.
            if (newDoc && newDoc.id) {
              setDocs((prev) => {
                if (prev.some((d) => d.id === newDoc.id)) return prev;
                return [newDoc, ...prev];
              });
            }
            setUploadOpen(false);
            await load({ background: true });
          }}
        />
      )}

      {viewingDoc && (
        <DocumentViewerModal
          studentId={studentId}
          document={viewingDoc}
          onClose={() => setViewingDoc(null)}
        />
      )}
    </Card>
  );
}
